import api, { tokenManager } from './api';

export interface ClientInfo {
  clientId: string;
  status: 'online' | 'offline';
  isOnline: boolean;
  lastSeen: string;
  lastHeartbeat: string;
  deviceInfo: {
    platform?: string;
    version?: string;
  };
}

export interface ClientStatusSummary {
  total: number;
  online: number;
  offline: number;
}

export interface ClientStatusResponse {
  clients: ClientInfo[];
  summary: ClientStatusSummary;
}

class ClientStatusService {
  private baseURL = '/client-status';
  private eventSource: EventSource | null = null;
  private listeners: Set<(status: ClientStatusResponse) => void> = new Set();

  async getClientStatus(): Promise<ClientStatusResponse> {
    try {
      const response = await api.get(this.baseURL);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch client status:', error);
      // 에러 시 기본값 반환
      return {
        clients: [],
        summary: { total: 0, online: 0, offline: 0 }
      };
    }
  }

  // 상태 텍스트 반환
  getStatusText(summary: ClientStatusSummary): string {
    if (summary.online > 0) {
      return `클라이언트 연결 상태`;
    } else {
      return `클라이언트 연결 없음`;
    }
  }

  // 상태 색상 반환 (Ant Design 색상)
  getStatusColor(summary: ClientStatusSummary): string {
    return summary.online > 0 ? '#52c41a' : '#ff4d4f'; // 초록색 : 빨간색
  }

  // 상태 아이콘 반환
  getStatusIcon(summary: ClientStatusSummary): string {
    return summary.online > 0 ? '●' : '●'; // 동그라미
  }

  // SSE 연결 시작
  startSSEConnection(): void {
    if (this.eventSource) {
      this.eventSource.close();
    }

    // 백엔드 서버 URL로 SSE 연결
    // 로컬 개발: 포트 3000
    // Cloud Run: API 도메인 사용
    let backendUrl;
    if (window.location.hostname === 'localhost') {
      backendUrl = window.location.origin.replace(':3001', ':3000');
    } else {
      // Cloud Run 환경: API 도메인 사용
      backendUrl = process.env.REACT_APP_API_BASE_URL || 'https://lobby-content-api-908552951468.asia-northeast3.run.app';
    }

    // 인증 토큰을 쿼리 파라미터로 추가 (SSE는 헤더 커스터마이징 불가)
    const token = tokenManager.getAccessToken();
    const sseUrl = token
      ? `${backendUrl}/api/cms/client-status/events?token=${encodeURIComponent(token)}`
      : `${backendUrl}/api/cms/client-status/events`;

    this.eventSource = new EventSource(sseUrl);

    this.eventSource.onopen = () => {
      console.log('SSE connection opened');
    };

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'client-status-update') {
          // console.log('Client status updated:', data.data.summary);
          // 모든 리스너에게 상태 업데이트 알림
          this.listeners.forEach(listener => {
            try {
              listener(data.data);
            } catch (listenerError) {
              console.error('Listener error:', listenerError);
            }
          });
        }
      } catch (error) {
        console.error('SSE message parsing error:', error);
      }
    };

    this.eventSource.onerror = (error) => {
      console.error('SSE error:', error);

      // 연결 재시도 (3초 후)
      if (this.eventSource?.readyState === EventSource.CLOSED) {
        setTimeout(() => {
          this.startSSEConnection();
        }, 3000);
      }
    };
  }

  // SSE 연결 종료
  stopSSEConnection(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.listeners.clear();
  }

  // 상태 변경 리스너 추가
  addStatusChangeListener(listener: (status: ClientStatusResponse) => void): void {
    this.listeners.add(listener);
  }

  // 상태 변경 리스너 제거
  removeStatusChangeListener(listener: (status: ClientStatusResponse) => void): void {
    this.listeners.delete(listener);
  }
}

const clientStatusService = new ClientStatusService();
export default clientStatusService;